const PLACEHOLDER_THRESHOLD = '__THRESHOLD__';
const PLACEHOLDER_REMAINING = '__REMAINING__';

class CartShippingThreshold extends HTMLElement {
  constructor() {
    super();
    this._cartUnsub = null;
    this._cartRemoveUnsub = null;
    this._cartQtyUnsub = null;
    this._cartSwatchUnsub = null;
  }

  connectedCallback() {
    this.ensureLayout();
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      if (!this._cartUnsub) {
        this._cartUnsub = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
          this.refresh();
        });
      }

      if (!this._cartRemoveUnsub) {
        this._cartRemoveUnsub = window.subscribe(window.PUB_SUB_EVENTS.cartRemove, () => {
          setTimeout(() => {
            this.refresh();
          }, 1000);
        });
      }

      if (!this._cartQtyUnsub) {
        this._cartQtyUnsub = window.subscribe(window.PUB_SUB_EVENTS.cartQtyUpdate, () => {
          setTimeout(() => {
            this.refresh();
          }, 1000);
        });
      }

      // Listen for swatch changes (color swatches in cart/edit flows)
      if (!this._cartSwatchUnsub) {
        this._cartSwatchUnsub = window.subscribe(window.PUB_SUB_EVENTS.swatchChange, () => {
          setTimeout(() => {
            this.refresh();
          }, 1000);
        });
      }
    }

    this.refresh();
  }

  disconnectedCallback() {
    if (typeof this._cartUnsub === 'function') {
      this._cartUnsub();
      this._cartUnsub = null;
    }
    if (typeof this._cartRemoveUnsub === 'function') {
      this._cartRemoveUnsub();
      this._cartRemoveUnsub = null;
    }
    if (typeof this._cartQtyUnsub === 'function') {
      this._cartQtyUnsub();
      this._cartQtyUnsub = null;
    }
    if (typeof this._cartSwatchUnsub === 'function') {
      this._cartSwatchUnsub();
      this._cartSwatchUnsub = null;
    }
  }

  ensureLayout() {
    if (this.querySelector('[data-ref="shipping-message-root"]')) return;

    const wrap = document.createElement('div');
    wrap.className = 'notification-card_shipping-message fixed-body-s';
    wrap.dataset.ref = 'shipping-message-root';

    if (this.hasAttribute('data-hide-progress')) {
      wrap.innerHTML = `
        <div data-ref="message"></div>
      `;
    } else {
      wrap.innerHTML = `
        <div data-ref="message"></div>
        <div class="shared-progress-bar-container">
          <div class="shared-progress-bar" data-ref="progress" style="width:0%"></div>
        </div>
      `;
    }
    this.insertBefore(wrap, this.firstChild);
  }

  /**
   * @param {string} name
   * @returns {string}
   */
  getTemplate(name) {
    const el = this.querySelector(`template[data-template="${name}"]`);
    return el ? el.innerHTML.trim() : '';
  }

  /**
   * @param {number} cents
   * @param {string} [currency]
   * @returns {string}
   */
  formatMoney(currency, cents) {
    return window.utilities.formatPrice(currency, cents / 100);
  }

  /**
   * @param {number} cents
   * @param {string} [currency]
   */
  remainingHtml(cents, currency) {
    return `<span class="em fixed-utility-m-em">${this.formatMoney(currency, cents)}</span>`;
  }

  /**
   * @param {string} template
   * @param {string} thresholdStr
   * @param {string} [remainingHtmlStr]
   */
  applyPlaceholders(template, thresholdStr, remainingHtmlStr = '') {
    return template.split(PLACEHOLDER_THRESHOLD).join(thresholdStr).split(PLACEHOLDER_REMAINING).join(remainingHtmlStr);
  }

  cartJsonUrl() {
    const base = window.routes?.cart_url || '/cart';
    return `${base}.js`;
  }

  /**
   * @returns {Promise<{ total_price: number; currency?: string } | null>}
   */
  async fetchCart() {
    try {
      const res = await fetch(this.cartJsonUrl());
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  /**
   * Sum line totals for in-stock cart rows rendered in the DOM.
   * @returns {number | null}
   */
  getEligibleTotalFromDom() {
    const holders = document.querySelectorAll('.cart-item__holder');
    if (!holders.length) return null;

    let total = 0;
    for (const holder of holders) {
      if (holder.classList.contains('cart-item__holder--sold-out')) continue;
      const cents = Number(holder.dataset.finalLinePriceCents);
      if (!Number.isFinite(cents)) return null;
      total += cents;
    }

    return total;
  }

  /**
   * Server-rendered eligible total from cart-drawer (excludes sold-out lines).
   * @returns {Promise<number | null>}
   */
  async fetchEligibleTotalFromCartDrawerSection() {
    try {
      const base = window.routes?.cart_url || '/cart';
      const url = `${base}?section_id=cart-drawer`;
      const res = await fetch(url);
      if (!res.ok) return null;

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const drawer = doc.querySelector('cart-drawer[data-eligible-total-cents]');
      if (!drawer) return null;

      const cents = Number(drawer.dataset.eligibleTotalCents);
      return Number.isFinite(cents) ? cents : null;
    } catch {
      return null;
    }
  }

  /**
   * Cart subtotal that counts only in-stock line items (matches Liquid cart-drawer logic).
   * @returns {Promise<number>}
   */
  async fetchEligibleTotalCents() {
    const fromDom = this.getEligibleTotalFromDom();
    if (fromDom !== null) return fromDom;

    const fromSection = await this.fetchEligibleTotalFromCartDrawerSection();
    if (fromSection !== null) return fromSection;

    const initialCents = Number(this.dataset.eligibleTotalCents);
    if (Number.isFinite(initialCents)) return initialCents;

    const cart = await this.fetchCart();
    return cart?.total_price ?? 0;
  }

  /**
   * @param {string} messageHtml
   * @param {number} percent
   */
  renderState(messageHtml, percent) {
    this.ensureLayout();
    const root = this.querySelector('[data-ref="shipping-message-root"]');
    const msg = root?.querySelector('[data-ref="message"]');
    const bar = root?.querySelector('[data-ref="progress"]');
    if (msg) msg.innerHTML = messageHtml;
    if (bar) {
      const p = Math.min(100, Math.max(0, Math.round(percent)));
      bar.style.width = `${p}%`;
    }

    // Announcement bar: keep sticky spacer in sync if copy length changes after hydrate.
    if (this.hasAttribute('data-hide-progress')) {
      document.dispatchEvent(new CustomEvent('sticky-banner:remeasure'));
    }
  }

  async refresh() {
    const allShipping = Number(this.dataset.allShippingCents ?? 0);

    if (allShipping <= 0) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    const cart = await this.fetchCart();
    const totalPrice = await this.fetchEligibleTotalCents();
    const currency = cart?.currency || this.dataset.currency || window.Shopify?.currency?.active;

    if (allShipping > 0) {
      const toFree = allShipping - totalPrice;
      if (toFree > 0) {
        const thresholdStr = this.formatMoney(currency, allShipping);
        const remaining = this.remainingHtml(toFree, currency);
        const template = this.getTemplate('all-msg');
        const percent = Math.round((totalPrice / allShipping) * 100);
        this.renderState(this.applyPlaceholders(template, thresholdStr, remaining), percent);
      } else {
        this.renderState(this.getTemplate('active'), 100);
      }
    }
  }
}

customElements.define('cart-shipping-threshold', CartShippingThreshold);
